import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useNavigate } from 'react-router-dom';
import AMPageShell from './components/AMPageShell';
import ReviewStatusBadge from './components/ReviewStatusBadge';
import Icon from 'components/AppIcon';
import { ASSETS, UPCOMING_REVIEWS } from './data/assetData';

const reviewCoverageData = [
  { period: 'Q1 2025', target: 8, completed: 8 },
  { period: 'Q2 2025', target: 8, completed: 7 },
  { period: 'Q3 2025', target: 8, completed: 6 },
  { period: 'Q4 2025', target: 8, completed: 4 },
];

const KPI_CARDS = [
  { label: 'Assets Tracked', value: 8, icon: 'Layers', colour: 'var(--color-primary)', sub: 'Across 2 funds' },
  { label: 'Reviews Completed (YTD)', value: 25, icon: 'CheckCircle', colour: 'var(--color-success)', sub: 'Target: 32' },
  { label: 'Reviews Due (Next 90 Days)', value: 6, icon: 'Clock', colour: 'var(--color-warning)', sub: 'Next: 15 Jan 2026' },
  { label: 'Assets on Watchlist', value: 2, icon: 'AlertCircle', colour: 'var(--color-warning)', sub: 'Tyne Bridge, Lisbon Metro' },
  { label: 'Items Escalated', value: 1, icon: 'AlertTriangle', colour: 'var(--color-error)', sub: 'Elbe Biomass Plant' },
  { label: 'Exit Cases', value: 1, icon: 'LogOut', colour: 'var(--color-primary)', sub: 'Nordic Fibre Network' },
];

const STATUS_COLOUR = {
  Draft: 'text-muted-foreground',
  Submitted: 'text-[#3b4c4c]',
  Reviewed: 'text-success',
  Closed: 'text-foreground',
};

const WATCHLIST_COLOURS = {
  'Confirmed': 'bg-green-100 text-green-800',
  'Confirmed with actions': 'bg-blue-100 text-blue-800',
  'Watchlist': 'bg-amber-100 text-amber-800',
  'Escalated': 'bg-red-100 text-red-800',
  'Exit case': 'bg-purple-100 text-purple-800',
};

const RISK_COLOURS = ['', 'text-green-600', 'text-green-500', 'text-amber-500', 'text-orange-500', 'text-red-600'];

export default function OperationsConsole({ selectedAssets, showToast }) {
  const assets = selectedAssets || ASSETS;
  const navigate = useNavigate();
  return (
    <AMPageShell activeTab="/asset-management/operations-console">
      <div className="space-y-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {KPI_CARDS?.map((kpi) => (
            <div key={kpi?.label} className="bg-card border border-border rounded-lg p-4 shadow-xs card-interactive">
              <div className="flex items-center justify-between mb-3">
                <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: kpi?.colour + '18' }}>
                  <Icon name={kpi?.icon} size={16} color={kpi?.colour} strokeWidth={2} />
                </div>
              </div>
              <p className="font-data text-2xl font-semibold text-foreground">{kpi?.value}</p>
              <p className="font-caption text-xs font-medium text-foreground mt-0.5">{kpi?.label}</p>
              <p className="font-caption text-xs text-muted-foreground mt-0.5">{kpi?.sub}</p>
            </div>
          ))}
        </div>

        {/* Portfolio Assets — Asset Detail Drill-down entry point */}
        <div className="bg-card border border-border rounded-lg shadow-xs overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <div>
              <h2 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.1rem' }}>Portfolio Assets</h2>
              <p className="font-caption text-xs text-muted-foreground mt-0.5">Click any asset to open the full IC-grade Asset Detail Drill-down</p>
            </div>
            <span className="text-xs font-caption text-muted-foreground bg-muted/40 px-2.5 py-1 rounded-full">{assets?.length} assets</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-0 divide-y md:divide-y-0 md:divide-x divide-border">
            {assets?.map((asset) => (
              <button
                key={asset?.id}
                onClick={() => navigate(`/portfolio/assets/${asset?.id}`, { state: { from: 'asset-management', backPath: '/asset-management/console', backLabel: 'Operations Console' } })}
                className="text-left p-5 hover:bg-primary/5 transition-colors group border-b border-border last:border-b-0 md:last:border-b md:border-b"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="font-caption text-sm font-semibold text-foreground group-hover:text-primary transition-colors leading-tight">{asset?.name}</p>
                  <Icon name="ArrowUpRight" size={14} color="var(--color-muted-foreground)" className="flex-shrink-0 mt-0.5 group-hover:text-primary" />
                </div>
                <p className="font-caption text-xs text-muted-foreground mb-3">{asset?.sector} · {asset?.country}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-caption font-semibold ${WATCHLIST_COLOURS?.[asset?.watchlistStatus] || 'bg-gray-100 text-gray-700'}`}>
                    {asset?.watchlistStatus}
                  </span>
                  <span className={`text-xs font-data font-bold ${RISK_COLOURS?.[asset?.riskScore]}`}>Risk {asset?.riskScore}/5</span>
                  <span className="text-xs font-data text-muted-foreground">{asset?.irr}% IRR</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Review Coverage Chart */}
        <div className="bg-card border border-border rounded-lg p-6 shadow-xs">
          <h2 className="font-heading font-semibold text-foreground mb-1" style={{ fontSize: '1.1rem' }}>Review Coverage</h2>
          <p className="font-caption text-xs text-muted-foreground mb-5">Annual review target vs completed by quarter</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={reviewCoverageData} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="period" tick={{ fontSize: 12, fontFamily: 'IBM Plex Sans' }} />
              <YAxis tick={{ fontSize: 12, fontFamily: 'IBM Plex Sans' }} />
              <Tooltip contentStyle={{ fontFamily: 'IBM Plex Sans', fontSize: 12 }} />
              <Legend wrapperStyle={{ fontFamily: 'IBM Plex Sans', fontSize: 12 }} />
              <Bar dataKey="target" name="Target" fill="var(--color-muted-foreground)" radius={[3, 3, 0, 0]} />
              <Bar dataKey="completed" name="Completed" fill="var(--color-primary)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Upcoming Reviews Table */}
        <div className="bg-card border border-border rounded-lg shadow-xs overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.1rem' }}>Upcoming Reviews</h2>
            <p className="font-caption text-xs text-muted-foreground mt-0.5">Next 90 days</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  {['Asset', 'Trigger Reason', 'Review Date', 'Owner', 'Status']?.map((h) => (
                    <th key={h} className="px-4 py-3 text-left font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {UPCOMING_REVIEWS?.map((r, i) => {
                  const matchedAsset = ASSETS?.find((a) => a?.name === r?.asset);
                  return (
                    <tr key={i} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => matchedAsset && navigate(`/asset-management/asset-detail/${matchedAsset?.id}`, { state: { from: 'asset-management', backPath: '/asset-management/console', backLabel: 'Operations Console' } })}
                          className={`font-caption text-sm font-medium ${matchedAsset ? 'text-primary hover:underline cursor-pointer' : 'text-foreground'}`}
                        >
                          {r?.asset}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-caption font-medium">{r?.triggerReason}</span>
                      </td>
                      <td className="px-4 py-3 font-data text-sm text-foreground">{r?.reviewDate}</td>
                      <td className="px-4 py-3 font-caption text-sm text-foreground">{r?.owner}</td>
                      <td className="px-4 py-3"><ReviewStatusBadge status={r?.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AMPageShell>
  );
}
